import React, { useEffect, useState } from 'react';
import SelfInspectQuestionLabel from '../SelfInspectQuestionLabel';
import SelfInspectBackButton from '../../../pages/self-inspect/SelfInspectBackButton';
import SelfInspectButton from '../SelfInspectButton';
import classnames from 'classnames';

const FarmOutbuildingsReview = ({
  classes,
  BOTTOM_PANEL_TRANSITION,
  selfInspectIntro,
  hideBackButton,
  goBack,
  handleAdditionalStructureNo,
  addingOutbuilding,
  handleAdditionalStructureYes,
  outbuildingSteps,
  viewOutbuilding,
  showNoOutbuildingsError,
}) => {
  // #region STATE
  const [outbuildingSummaries, setOutbuildingSummaries] = useState([]);
  // #endregion STATE

  // #region HOOKS
  // #endregion HOOKS

  // #region COMPUTED
  const errorsExist = outbuildingSummaries.some(summary => summaryHasErrors(summary));
  const hasOutbuildings = outbuildingSummaries.length > 0;
  // #endregion COMPUTED

  // #region FUNCTIONS
  function summaryHasErrors(summary) {
    return !summary.structureType || !summary.frontPics?.length || !summary.rearPics?.length;
  }
  // #endregion FUNCTIONS

  // #region EFFECTS
  useEffect(() => {
    const stepsGroupedByOutbuildingNumber = outbuildingSteps.reduce((acc, ob) => {
      if (!acc[ob.outbuildingNumber]) {
        acc[ob.outbuildingNumber] = [];
      }

      acc[ob.outbuildingNumber].push(ob);

      return acc;
    }, {});

    const summaries = Object.keys(stepsGroupedByOutbuildingNumber).map((number, index) => {
      const outbuildingNumber = Number(number);

      const steps = stepsGroupedByOutbuildingNumber[outbuildingNumber];

      const structureTypeStep = steps.find(s => s.questionId === 7244);
      const selectedStructureTypeOption = structureTypeStep?.options?.find(
        opt => opt.value === structureTypeStep.answerValue,
      );

      const descriptionStep = steps.find(s => s.questionId === 2122);

      const frontPicStep = steps.find(s => s.questionId === 7245);

      const rearPicStep = steps.find(s => s.questionId === 7246);

      const summary = {
        timestamp: new Date().getTime(),
        outbuildingIndex: index,
        outbuildingNumber,
        structureType: selectedStructureTypeOption?.label,
        descrption: descriptionStep?.answerValue,
        frontPics: frontPicStep?.answerValue,
        rearPics: rearPicStep?.answerValue,
      };

      return summary;
    });

    // console.log('summaries', outbuildingSteps, summaries);

    setOutbuildingSummaries(summaries);
  }, [outbuildingSteps]);

  // #endregion EFFECTS

  return (
    <div className={classes.container}>
      <div className={classes.container}>
        <div
          style={{
            maxHeight: '100vh',
            transition: BOTTOM_PANEL_TRANSITION,
          }}
        >
          <SelfInspectQuestionLabel
            isHeader
            style={{
              marginTop: 20,
            }}
          >
            Farm Structures Overview
          </SelfInspectQuestionLabel>
          <div className="text-center mb-10" style={{ opacity: 0.75 }}>
            (click on line item to view)
          </div>
        </div>

        {!hasOutbuildings && (
          <div className="mb-10 text-center">
            Let's capture all farm structures you have on your property. You will be asked to answer a few questions and
            snap a couple photos of each structure.
          </div>
        )}

        <div
          className="flex-1 flex-column mb-20"
          style={{
            overflow: 'auto',
            fontSize: 14,
          }}
        >
          {outbuildingSummaries.map((summary, index) => {
            const hasErrors = summaryHasErrors(summary);
            const hasFrontPics = summary.frontPics?.length;
            const hasRearPics = summary.rearPics?.length;

            return (
              <div
                key={`${summary.outbuildingNumber}-${summary.timestamp}`}
                className={classnames('structure-review-container', hasErrors && 'error')}
                onClick={() => {
                  viewOutbuilding(summary.outbuildingNumber);
                }}
              >
                <div className="flex-row" style={{ columnGap: 20 }}>
                  <div className="row-start-center" style={{ width: '50%' }}>
                    <div className={classnames('structure-number', hasErrors && 'error')}>
                      {summary.outbuildingIndex + 1}
                    </div>

                    <div className="flex-1 flex-col">
                      <div className="text-bold">
                        {summary.structureType ?? <span className="red-text">Structure type</span>}
                      </div>
                      <div>{summary.descrption ?? <span style={{ opacity: 0.5 }}>No description</span>}</div>
                    </div>
                  </div>

                  <div
                    className={classnames('structure-review-image', !hasFrontPics && 'no-image')}
                    style={{
                      ...(hasFrontPics && {
                        backgroundImage: `url(${summary.frontPics[0].src})`,
                      }),
                    }}
                  >
                    {!hasFrontPics && <span className="red-text text-center">Image required</span>}
                  </div>

                  <div
                    className={classnames('structure-review-image', !hasRearPics && 'no-image')}
                    style={{
                      ...(hasRearPics && {
                        backgroundImage: `url(${summary.rearPics[0].src})`,
                      }),
                    }}
                  >
                    {!hasRearPics && <span className="red-text text-center">Image required</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!hasOutbuildings && showNoOutbuildingsError && (
        <div className="text-red text-center mb-10">Must have at least one structure to continue</div>
      )}

      <div className={classes.actionContainer}>
        {!hideBackButton && <SelfInspectBackButton onClick={goBack} className="mr-20" />}

        <SelfInspectButton
          //
          label={'Add Structure'}
          onClick={handleAdditionalStructureYes}
          className="flex-1 mr-20"
          disabled={addingOutbuilding}
        />

        <SelfInspectButton
          //
          label={errorsExist || !hasOutbuildings ? 'Fix Errors...' : 'All Done!'}
          onClick={handleAdditionalStructureNo}
          className="flex-1"
          disabled={addingOutbuilding || errorsExist || !hasOutbuildings}
        />
      </div>
    </div>
  );
};

export default FarmOutbuildingsReview;
